import React from 'react';
import { Avatar } from 'primereact/avatar';
import { Chip } from 'primereact/chip';
import { Divider } from 'primereact/divider';
import { format, isToday, isYesterday, differenceInDays, differenceInHours, differenceInMinutes } from 'date-fns';

interface ChatMessage {
	id: string;
	chatRole: string;
	message: string;
	timestamp: Date;
	userName: string;
	userAvatar?: string;
}

interface RemarkHistoryProps {
	messages: ChatMessage[];
}

const formatRelativeTime = (date: Date): string => {
	const now = new Date();
	const minutesDiff = differenceInMinutes(now, date);
	const hoursDiff = differenceInHours(now, date);
	const daysDiff = differenceInDays(now, date);

	if (minutesDiff < 60) {
		return `${minutesDiff} mins ago`;
	} else if (hoursDiff < 24) {
		return `${hoursDiff} hours ago`;
	} else if (daysDiff === 1) {
		return 'yesterday';
	} else if (daysDiff < 7) {
		return `${daysDiff} days ago`;
	} else {
		return format(date, 'dd/MM/yyyy');
	}
};

const groupMessagesByDate = (messages: ChatMessage[]) => {
	const groups: { [key: string]: ChatMessage[] } = {};

	messages.forEach(message => {
		const date = message.timestamp;
		let dateKey: string;

		if (isToday(date)) {
			dateKey = 'today';
		} else if (isYesterday(date)) {
			dateKey = 'yesterday';
		} else {
			dateKey = format(date, 'yyyy-MM-dd');
		}

		if (!groups[dateKey]) {
			groups[dateKey] = [];
		}
		groups[dateKey].push(message);
	});

	// Sort groups by date (most recent first)
	const sortedGroups = Object.entries(groups).sort(([dateA], [dateB]) => {
		if (dateA === 'today') return -1;
		if (dateB === 'today') return 1;
		if (dateA === 'yesterday') return -1;
		if (dateB === 'yesterday') return 1;
		return new Date(dateB).getTime() - new Date(dateA).getTime();
	});

	return sortedGroups;
};

const RemarkHistory: React.FC<RemarkHistoryProps> = ({ messages }) => {
	const groupedMessages = groupMessagesByDate(messages);

	const getInitials = (name: string) => {
		return name
			.split(' ')
			.map(n => n[0])
			.join('')
			.toUpperCase();
	};

	return (
		<div className="bg-chat-bg p-2 rounded-lg h-56 overflow-scroll show-scrollbar">
			{messages.length ? (
				<h1 className="text-xl font-bold text-foreground mb-6">{`Remark from ${messages[0]?.userName}`}</h1>
			) : (
				<h1 className="text-xl text-center font-bold text-foreground mb-6">{`No Remark`}</h1>
			)}

			<div className="space-y-4">
				{groupedMessages.map(([dateKey, dateMessages], groupIndex) => (
					<div key={dateKey}>
						{dateMessages.map((message, index) => {
							const prevMessage = dateMessages[index - 1];
							const showAvatar = !prevMessage || prevMessage.userName !== message.userName;
							return (
								<div key={message.id} className="flex items-start gap-4 py-1">
									{showAvatar ? (
										<Avatar
											image={message.userAvatar}
											label={getInitials(message.userName)}
											size="large"
											shape="circle"
											className="bg-avatar-bg text-avatar-fallback"
										/>
									) : (
										<div className="w-12" />
									)}

									<div className="flex-1 min-w-0">
										{showAvatar && (
											<div className="flex items-center gap-3 mt-3 mb-2">
												<h3 className="font-semibold text-foreground text-lg">
													{message.userName}
												</h3>
												<span className="text-muted-foreground text-sm plus-jakarta-sans-font">
													{formatRelativeTime(message.timestamp)}
												</span>
												{message.chatRole === 'ERP' && (
													<Chip
														label="HK Assistant"
														className="bg-assistant-tag text-assistant-tag-foreground text-xs"
													/>
												)}
											</div>
										)}

										<p className="text-foreground text-lg leading-relaxed whitespace-pre-wrap plus-jakarta-sans-font">
											{message.message}
										</p>
									</div>
								</div>
							);
						})}

						{groupIndex < groupedMessages.length - 1 && <Divider className="my-6 border-chat-separator" />}
					</div>
				))}
			</div>
		</div>
	);
};

export default RemarkHistory;
